import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { User, LoginCredentials, AuthSession } from "@/types/auth";
import { kvService } from "@/lib/kv-service";
import { cacheService } from "@/lib/cache-service";
import { generateUUID } from "@/lib/uuid";

// Always use Cloudflare KV for authentication
const authService = kvService;

// Simple password verification (in production, use proper hashing like bcrypt)
async function verifyPassword(username: string, password: string): Promise<boolean> {
  try {
    const response = await fetch(`${import.meta.env.VITE_KV_API_URL}/auth/verify`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_KV_TOKEN}`,
        'X-API-Token': import.meta.env.VITE_KV_TOKEN,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      console.error('Auth verify failed:', response.status);
      return false;
    }

    const result = await response.json();
    return result.valid === true;
  } catch (error) {
    console.error('Password verification failed:', error);
    return false;
  }
}

interface LoginResult {
  success: boolean;
  error?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isLoginLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<LoginResult>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoginLoading, setIsLoginLoading] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem("auth-token");
      if (!token) {
        setIsLoading(false);
        return;
      }

      const session = await authService.getSession(token);
      if (session && new Date() < session.expiresAt) {
        setUser(session.user);
      } else {
        localStorage.removeItem("auth-token");
        if (session) {
          await authService.deleteSession(token);
        }
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      localStorage.removeItem("auth-token");
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials: LoginCredentials): Promise<LoginResult> => {
    try {
      setIsLoginLoading(true);

      // پشکنینی بوونی بەکارهێنەر
      let user;
      try {
        user = await authService.getUser(credentials.username);
      } catch (error) {
        console.error("Failed to get user:", error);
        setIsLoginLoading(false);
        return { success: false, error: 'کێشە لە پەیوەندی بە سێرڤەر، تکایە دووبارە هەوڵ بدەرەوە' };
      }

      if (!user) {
        setIsLoginLoading(false);
        return { success: false, error: 'ناوی بەکارهێنەر یان وشەی نهێنی هەڵەیە' };
      }

      // پشکنینی پاسوۆرد
      let validPassword;
      try {
        validPassword = await verifyPassword(credentials.username, credentials.password);
      } catch (error) {
        console.error("Password verification request failed:", error);
        setIsLoginLoading(false);
        return { success: false, error: 'کێشە لە پەیوەندی بە سێرڤەر، تکایە دووبارە هەوڵ بدەرەوە' };
      }

      if (!validPassword) {
        setIsLoginLoading(false);
        return { success: false, error: 'ناوی بەکارهێنەر یان وشەی نهێنی هەڵەیە' };
      }

      // دروستکردنی سیشن
      const token = generateUUID();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      const session: AuthSession = {
        user,
        token,
        expiresAt,
      };

      try {
        await authService.createSession(session);
        await authService.updateUserLastLogin(credentials.username);
      } catch (error) {
        console.error("Session creation failed:", error);
        setIsLoginLoading(false);
        return { success: false, error: 'کێشە لە تۆمارکردنی چوونەژوورەوە' };
      }

      localStorage.setItem("auth-token", token);
      setUser(user);
      setIsLoginLoading(false);

      return { success: true };
    } catch (error) {
      console.error("Login failed unexpectedly:", error);
      setIsLoginLoading(false);
      return { success: false, error: 'هەڵەیەکی چاوەڕوان نەکراو ڕوویدا' };
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("auth-token");
      if (token) {
        await authService.deleteSession(token);
        localStorage.removeItem("auth-token");
      }
      // پاککردنەوەی کاشی ناوخۆیی
      await cacheService.clearAll();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isLoginLoading,
    login,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}